/** Data hooks for Phase 5: pricing (CPQ), sales orders, accounting, territories, dealers, visits. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, qs, tokenStore } from "./client";
import type {
  AccountingConnection, CpqPolicy, DealerLead, DealerMe, DealerOrder, Invoice, Order, OrderSummary, Page,
  PriceBook, PriceBookDetail, ProductUnit, ResolvedPrice, Territory, Visit,
} from "./types";

type Body = Record<string, unknown> | unknown[];
type Method = "POST" | "PATCH" | "PUT" | "DELETE";

function useInvalidate() {
  const qc = useQueryClient();
  return (...prefixes: string[]) => prefixes.forEach((p) => void qc.invalidateQueries({ queryKey: [p] }));
}

/** A mutation that calls any endpoint and refreshes the given query prefixes afterwards. */
export function useCall<T>(...refresh: string[]) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ method = "POST", path, body }: { method?: Method; path: string; body?: Body }) => api<T>(method, path, body),
    onSuccess: () => invalidate(...refresh),
  });
}

// ---- pricing ----

export const usePriceBooks = () => useQuery({ queryKey: ["price-books"], queryFn: () => api<PriceBook[]>("GET", "/price-books") });
export const usePriceBook = (id: string | null) =>
  useQuery({ queryKey: ["price-book", id], queryFn: () => api<PriceBookDetail>("GET", `/price-books/${id}`), enabled: !!id });
export const useUnits = (productId: string | undefined) =>
  useQuery({ queryKey: ["units", productId], queryFn: () => api<ProductUnit[]>("GET", `/products/${productId}/units`), enabled: !!productId });
export const usePolicy = () => useQuery({ queryKey: ["cpq-policy"], queryFn: () => api<CpqPolicy>("GET", "/cpq/policy") });

/** The price a customer gets for a product, quantity and unit. */
export async function resolvePrice(item: { product_id: string; quantity?: string; uom?: string | null }, accountId?: string | null, currency?: string) {
  const [price] = await api<ResolvedPrice[]>("POST", "/cpq/price", {
    account_id: accountId ?? null, currency: currency ?? null,
    items: [{ product_id: item.product_id, quantity: item.quantity && Number(item.quantity) > 0 ? item.quantity : "1", uom: item.uom || null }],
  });
  if (!price) throw new Error("No price returned");
  return price;
}

// ---- orders ----

export const useOrders = (filters: { status?: string; account_id?: string; deal_id?: string; q?: string; sync_status?: string }, enabled = true) =>
  useQuery({ queryKey: ["orders", filters], queryFn: () => api<Page<OrderSummary>>("GET", `/orders${qs({ ...filters, limit: 100 })}`), enabled });

export const useOrder = (id: string | undefined) =>
  useQuery({
    queryKey: ["order", id], queryFn: () => api<Order>("GET", `/orders/${id}`), enabled: !!id,
    // A queued order is picked up by the Tally agent or the Zoho job; watch for the result.
    refetchInterval: (query) => (query.state.data?.sync_status === "queued" ? 3000 : false),
  });

export function useOrderAction() {
  const qc = useQueryClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ method = "POST", path, body }: { method?: Method; path: string; body?: Body }) => api<Order>(method, path, body),
    onSuccess: (o) => { qc.setQueryData(["order", o.id], o); invalidate("orders", "quote", "timeline", "account-invoices"); },
  });
}

export const useAccountInvoices = (accountId: string) =>
  useQuery({ queryKey: ["account-invoices", accountId], queryFn: () => api<Invoice[]>("GET", `/accounts/${accountId}/invoices`) });

// ---- accounting ----

export const useAccounting = (poll = false) =>
  useQuery({
    queryKey: ["accounting"], queryFn: () => api<AccountingConnection[]>("GET", "/integrations/accounting"),
    refetchInterval: poll ? 5000 : false,
  });

// ---- territories and dealers ----

export const useTerritories = () => useQuery({ queryKey: ["territories"], queryFn: () => api<Territory[]>("GET", "/territories") });
export const useDealerMe = () => useQuery({ queryKey: ["dealer-me"], queryFn: () => api<DealerMe>("GET", "/dealer/me") });
export const useDealerLeads = () => useQuery({ queryKey: ["dealer-leads"], queryFn: () => api<DealerLead[]>("GET", "/dealer/leads") });
export const useDealerOrders = () => useQuery({ queryKey: ["dealer-orders"], queryFn: () => api<DealerOrder[]>("GET", "/dealer/orders") });

// ---- visits ----

export const useVisits = (filters: { day?: string; mine?: boolean; account_id?: string }) =>
  useQuery({ queryKey: ["visits", filters], queryFn: () => api<Visit[]>("GET", `/visits${qs(filters)}`) });

/** Download an authenticated file (for example the Tally agent). */
export async function downloadFile(path: string, filename: string): Promise<void> {
  const res = await fetch(`/api/v1${path}`, { headers: { Authorization: `Bearer ${tokenStore.get() ?? ""}` } });
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  const url = URL.createObjectURL(await res.blob());
  const a = Object.assign(document.createElement("a"), { href: url, download: filename });
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
